import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Video, FileText, Utensils, Dumbbell, ChevronDown, ChevronUp, X, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProgramContent = () => {
  const [activeLevel, setActiveLevel] = useState('nivel_1');
  const [activeTab, setActiveTab] = useState('routines'); // 'routines' or 'nutrition'
  const [programContent, setProgramContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Estado para editar rutinas
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [routineForm, setRoutineForm] = useState({
    day: 1,
    title: '',
    description: '',
    video_url: '',
    exercises: []
  });
  
  // Estado para editar nutrición
  const [nutritionForm, setNutritionForm] = useState({
    description: '',
    plans: []
  });

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const levels = [
    { id: 'nivel_1', name: 'Nivel 1 - Principiante', days: 4, color: 'bg-blue-500' },
    { id: 'nivel_2', name: 'Nivel 2 - Intermedio', days: 5, color: 'bg-purple-500' },
    { id: 'nivel_3', name: 'Nivel 3 - Avanzado', days: 6, color: 'bg-orange-500' },
  ];

  useEffect(() => {
    fetchProgramContent();
  }, [activeLevel]);

  const fetchProgramContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/program-content/${activeLevel}`, { headers });
      setProgramContent(response.data);
      
      // Inicializar formulario de nutrición
      setNutritionForm({
        description: response.data.nutrition_description || '',
        plans: response.data.nutrition_plans || []
      });
    } catch (error) {
      console.error('Error fetching program content:', error);
      setProgramContent({ level: activeLevel, routines: [], nutrition_plans: [] });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLevel = () => levels.find(l => l.id === activeLevel);

  // ===== RUTINAS =====
  const openRoutineEditor = (routine = null) => {
    if (routine) {
      setRoutineForm({ ...routine });
      setEditingRoutine(routine.day);
    } else {
      const existingDays = (programContent?.routines || []).map(r => r.day);
      const nextDay = [...Array(getCurrentLevel().days)].map((_, i) => i + 1).find(d => !existingDays.includes(d)) || 1;
      setRoutineForm({
        day: nextDay,
        title: `Día ${nextDay} - Entrenamiento`,
        description: '',
        video_url: '',
        exercises: []
      });
      setEditingRoutine('new');
    }
  };

  const addExercise = () => {
    setRoutineForm({
      ...routineForm,
      exercises: [...routineForm.exercises, { name: '', sets: '', reps: '', notes: '' }]
    });
  };

  const updateExercise = (index, field, value) => {
    const updated = [...routineForm.exercises];
    updated[index][field] = value;
    setRoutineForm({ ...routineForm, exercises: updated });
  };

  const removeExercise = (index) => {
    setRoutineForm({
      ...routineForm,
      exercises: routineForm.exercises.filter((_, i) => i !== index)
    });
  };

  const saveRoutine = async () => {
    if (!routineForm.title) {
      toast.error('El título es requerido');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine`,
        routineForm,
        { headers }
      );
      toast.success('Rutina guardada exitosamente');
      setEditingRoutine(null);
      fetchProgramContent();
    } catch (error) {
      toast.error('Error al guardar la rutina');
    } finally {
      setSaving(false);
    }
  };

  const deleteRoutine = async (day) => {
    if (!window.confirm(`¿Eliminar la rutina del día ${day}?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${day}`, { headers });
      toast.success('Rutina eliminada');
      fetchProgramContent();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // ===== NUTRICIÓN =====
  const addNutritionPlan = () => {
    const planTypes = activeLevel === 'nivel_3' 
      ? ['Masa Muscular 1', 'Masa Muscular 2', 'Masa Muscular 3', 'Reducción Grasa 1', 'Reducción Grasa 2', 'Reducción Grasa 3']
      : activeLevel === 'nivel_2'
      ? ['Menú 1', 'Menú 2', 'Menú 3', 'Menú 4', 'Menú 5']
      : ['Plan Personalizado'];
    
    const existingNames = nutritionForm.plans.map(p => p.name);
    const nextPlan = planTypes.find(t => !existingNames.includes(t)) || `Plan ${nutritionForm.plans.length + 1}`;
    
    setNutritionForm({
      ...nutritionForm,
      plans: [...nutritionForm.plans, {
        name: nextPlan,
        type: activeLevel === 'nivel_3' ? (nextPlan.includes('Masa') ? 'mass_gain' : 'fat_loss') : 'general',
        meals: [],
        pdf_url: ''
      }]
    });
  };

  const updateNutritionPlan = (index, field, value) => {
    const updated = [...nutritionForm.plans];
    updated[index][field] = value;
    setNutritionForm({ ...nutritionForm, plans: updated });
  };

  const addMealToPlan = (planIndex) => {
    const updated = [...nutritionForm.plans];
    updated[planIndex].meals = [...(updated[planIndex].meals || []), {
      name: '',
      time: '',
      foods: ''
    }];
    setNutritionForm({ ...nutritionForm, plans: updated });
  };

  const updateMeal = (planIndex, mealIndex, field, value) => {
    const updated = [...nutritionForm.plans];
    updated[planIndex].meals[mealIndex][field] = value;
    setNutritionForm({ ...nutritionForm, plans: updated });
  };

  const removeMeal = (planIndex, mealIndex) => {
    const updated = [...nutritionForm.plans];
    updated[planIndex].meals = updated[planIndex].meals.filter((_, i) => i !== mealIndex);
    setNutritionForm({ ...nutritionForm, plans: updated });
  };

  const removeNutritionPlan = (index) => {
    setNutritionForm({
      ...nutritionForm,
      plans: nutritionForm.plans.filter((_, i) => i !== index)
    });
  };

  const saveNutrition = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/nutrition`,
        nutritionForm,
        { headers }
      );
      toast.success('Plan nutricional guardado');
      fetchProgramContent();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Gestión de Programas</h1>
          <p className="text-zinc-400">Administra rutinas de entrenamiento y planes nutricionales por nivel</p>
        </div>

        {/* Level Selector */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {levels.map((level) => (
            <button
              key={level.id}
              onClick={() => setActiveLevel(level.id)}
              className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                activeLevel === level.id
                  ? `${level.color} text-white`
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              {level.name} ({level.days} días)
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('routines')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium ${
              activeTab === 'routines' ? 'bg-pink-600 text-white' : 'bg-zinc-900 text-zinc-400'
            }`}
          >
            <Dumbbell className="w-5 h-5" />
            Rutinas de Entrenamiento
          </button>
          <button
            onClick={() => setActiveTab('nutrition')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium ${
              activeTab === 'nutrition' ? 'bg-green-600 text-white' : 'bg-zinc-900 text-zinc-400'
            }`}
          >
            <Utensils className="w-5 h-5" />
            Planes Nutricionales
          </button>
        </div>

        {/* Contenido de Rutinas */}
        {activeTab === 'routines' && (
          <div className="space-y-4">
            {/* Lista de días */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(getCurrentLevel().days)].map((_, idx) => {
                const day = idx + 1;
                const routine = (programContent?.routines || []).find(r => r.day === day);
                
                return (
                  <div key={day} className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${getCurrentLevel().color} text-white rounded-xl flex items-center justify-center font-bold`}>
                          {day}
                        </div>
                        <div>
                          <h3 className="font-bold">{routine?.title || `Día ${day}`}</h3>
                          <p className="text-sm text-zinc-500">
                            {routine ? `${routine.exercises?.length || 0} ejercicios` : 'Sin contenido'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {routine?.video_url && (
                      <div className="mb-3 p-2 bg-pink-50 rounded-lg text-sm text-pink-600 flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Video agregado
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openRoutineEditor(routine || { day })}
                        variant="outline"
                        className="flex-1 rounded-lg"
                        size="sm"
                      >
                        {routine ? 'Editar' : 'Crear'}
                      </Button>
                      {routine && (
                        <Button
                          onClick={() => deleteRoutine(day)}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 rounded-lg"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contenido de Nutrición */}
        {activeTab === 'nutrition' && (
          <div className="space-y-6">
            {/* Descripción general */}
            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
              <h3 className="font-bold mb-3">Descripción del Plan Nutricional</h3>
              <textarea
                value={nutritionForm.description}
                onChange={(e) => setNutritionForm({ ...nutritionForm, description: e.target.value })}
                placeholder="Descripción general del plan nutricional para este nivel..."
                rows={3}
                className="w-full px-4 py-2 border rounded-xl resize-none"
              />
            </div>

            {/* Lista de planes */}
            <div className="space-y-4">
              {nutritionForm.plans.map((plan, planIdx) => (
                <div key={planIdx} className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        plan.type === 'mass_gain' ? 'bg-blue-100 text-blue-600' : 
                        plan.type === 'fat_loss' ? 'bg-orange-100 text-orange-600' : 
                        'bg-green-100 text-green-600'
                      }`}>
                        <Utensils className="w-5 h-5" />
                      </div>
                      <Input
                        value={plan.name}
                        onChange={(e) => updateNutritionPlan(planIdx, 'name', e.target.value)}
                        className="font-bold border-0 text-lg p-0 h-auto"
                        placeholder="Nombre del plan"
                      />
                    </div>
                    <Button
                      onClick={() => removeNutritionPlan(planIdx)}
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* URL del PDF */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">URL del PDF o Video</label>
                    <Input
                      value={plan.pdf_url || ''}
                      onChange={(e) => updateNutritionPlan(planIdx, 'pdf_url', e.target.value)}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                  </div>

                  {/* Comidas */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm text-zinc-500">Comidas del día</h4>
                      <Button onClick={() => addMealToPlan(planIdx)} variant="ghost" size="sm">
                        <Plus className="w-4 h-4 mr-1" /> Agregar Comida
                      </Button>
                    </div>
                    
                    {(plan.meals || []).map((meal, mealIdx) => (
                      <div key={mealIdx} className="grid grid-cols-12 gap-2 items-center bg-[#09090b] p-3 rounded-xl">
                        <div className="col-span-3">
                          <Input
                            value={meal.name}
                            onChange={(e) => updateMeal(planIdx, mealIdx, 'name', e.target.value)}
                            placeholder="Ej: Desayuno"
                            className="text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={meal.time}
                            onChange={(e) => updateMeal(planIdx, mealIdx, 'time', e.target.value)}
                            placeholder="7:00 AM"
                            className="text-sm"
                          />
                        </div>
                        <div className="col-span-6">
                          <Input
                            value={meal.foods}
                            onChange={(e) => updateMeal(planIdx, mealIdx, 'foods', e.target.value)}
                            placeholder="Alimentos..."
                            className="text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            onClick={() => removeMeal(planIdx, mealIdx)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Agregar plan */}
            <Button
              onClick={addNutritionPlan}
              variant="outline"
              className="w-full border-2 border-dashed py-6 rounded-2xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Plan Nutricional
            </Button>

            {/* Guardar */}
            <Button
              onClick={saveNutrition}
              disabled={saving}
              className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6 font-bold"
            >
              {saving ? 'Guardando...' : 'Guardar Planes Nutricionales'}
            </Button>
          </div>
        )}
      </div>

      {/* Modal de edición de rutina */}
      {editingRoutine && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-2xl my-8">
            <div className="p-6 border-b border-zinc-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {editingRoutine === 'new' ? 'Nueva Rutina' : `Editar Día ${routineForm.day}`}
                </h2>
                <button onClick={() => setEditingRoutine(null)} className="p-2 hover:bg-zinc-800 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Día</label>
                  <select
                    value={routineForm.day}
                    onChange={(e) => setRoutineForm({ ...routineForm, day: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                  >
                    {[...Array(getCurrentLevel().days)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>Día {i + 1}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Título</label>
                  <Input
                    value={routineForm.title}
                    onChange={(e) => setRoutineForm({ ...routineForm, title: e.target.value })}
                    placeholder="Ej: Piernas y Glúteos"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <textarea
                  value={routineForm.description}
                  onChange={(e) => setRoutineForm({ ...routineForm, description: e.target.value })}
                  placeholder="Descripción de la rutina..."
                  rows={2}
                  className="w-full px-4 py-2 border rounded-xl resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Video de Entrenamiento</label>
                <div className="space-y-3">
                  {/* Opción de URL */}
                  <div className="flex items-center gap-2">
                    <Input
                      value={routineForm.video_url}
                      onChange={(e) => setRoutineForm({ ...routineForm, video_url: e.target.value })}
                      placeholder="URL del video (YouTube, Vimeo, o enlace directo)"
                      className="rounded-xl flex-1"
                    />
                  </div>
                  <p className="text-xs text-zinc-500">
                    Puedes pegar una URL de YouTube, Vimeo, o un enlace directo al video.
                    También puedes subir videos desde "Subir Contenido" y luego copiar la URL aquí.
                  </p>
                  {routineForm.video_url && (
                    <div className="p-3 bg-green-50 rounded-xl flex items-center gap-2 text-green-700">
                      <Video className="w-5 h-5" />
                      <span className="text-sm">Video configurado</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ejercicios */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="font-medium">Ejercicios</label>
                  <Button onClick={addExercise} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {routineForm.exercises.map((ex, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-[#09090b] p-3 rounded-xl">
                      <div className="col-span-4">
                        <Input
                          value={ex.name}
                          onChange={(e) => updateExercise(idx, 'name', e.target.value)}
                          placeholder="Nombre ejercicio"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={ex.sets}
                          onChange={(e) => updateExercise(idx, 'sets', e.target.value)}
                          placeholder="Series"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={ex.reps}
                          onChange={(e) => updateExercise(idx, 'reps', e.target.value)}
                          placeholder="Reps"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={ex.notes}
                          onChange={(e) => updateExercise(idx, 'notes', e.target.value)}
                          placeholder="Notas"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <button
                          onClick={() => removeExercise(idx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-100">
              <Button
                onClick={saveRoutine}
                disabled={saving}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-4 font-bold"
              >
                {saving ? 'Guardando...' : 'Guardar Rutina'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProgramContent;
